import React, { useState, useEffect } from 'react';
import api from '../api/axios';

const AdminSettings = () => {
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [paymentMode, setPaymentMode] = useState('gateway'); // 'gateway' (automático) ou 'pix_key' (manual)
  const [pixKey, setPixKey] = useState('');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [whatsappError, setWhatsappError] = useState('');
  const [discordWebhook, setDiscordWebhook] = useState('');
  const [telegramToken, setTelegramToken] = useState('');
  const [telegramChatId, setTelegramChatId] = useState('');
  const [userNoticeEnabled, setUserNoticeEnabled] = useState(false);
  const [userNoticeTitle, setUserNoticeTitle] = useState('');
  const [userNoticeMessage, setUserNoticeMessage] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      console.log('Settings loaded:', response.data);
      setPaymentMode(response.data.paymentMode === 'pix_key' ? 'pix_key' : 'gateway');
      setPixKey(response.data.pixKey || '');
      setWhatsappNumber(response.data.whatsappNumber || '');
      setDiscordWebhook(response.data.discordWebhook || '');
      setTelegramToken(response.data.telegramToken || '');
      setTelegramChatId(response.data.telegramChatId || '');
      setUserNoticeEnabled(response.data.userNoticeEnabled === 'true');
      setUserNoticeTitle(response.data.userNoticeTitle || '');
      setUserNoticeMessage(response.data.userNoticeMessage || '');
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  // Validate WhatsApp number format: 55+DDD+NUMBER (ex: 5511999999999)
  const validateWhatsappNumber = (number) => {
    // Remove any non-digit characters
    const cleaned = number.replace(/\D/g, '');
    
    // Check if starts with 55 and has correct length (13 or 14 digits)
    if (!cleaned.startsWith('55')) {
      return { valid: false, error: 'O número deve começar com 55 (código do Brasil)' };
    }
    
    if (cleaned.length < 12 || cleaned.length > 14) {
      return { valid: false, error: 'O número deve ter entre 12 e 14 dígitos (55 + DDD + número)' };
    }
    
    return { valid: true, cleaned };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setWhatsappError('');
    setSaving(true);

    try {
      // Validate WhatsApp number if provided
      if (whatsappNumber.trim()) {
        const validation = validateWhatsappNumber(whatsappNumber);
        if (!validation.valid) {
          setWhatsappError(validation.error);
          setSaving(false);
          return;
        }
        // Save with cleaned number
        await api.post('/settings', {
          key: 'whatsappNumber',
          value: validation.cleaned,
          description: 'Número do WhatsApp para suporte (formato: 55+DDD+NUMERO)'
        });
      } else {
        // Save empty value to clear
        await api.post('/settings', {
          key: 'whatsappNumber',
          value: '',
          description: 'Número do WhatsApp para suporte (formato: 55+DDD+NUMERO)'
        });
      }

      // Save Payment Mode (gateway/pix_key)
      await api.post('/settings', {
        key: 'paymentMode',
        value: paymentMode,
        description: 'Modo de pagamento: gateway (automático) ou pix_key (manual)'
      });

      // Save PIX key
      await api.post('/settings', {
        key: 'pixKey',
        value: pixKey,
        description: 'Chave PIX para pagamento de créditos'
      });

      // Save Discord Webhook
      await api.post('/settings', {
        key: 'discordWebhook',
        value: discordWebhook.trim(),
        description: 'Webhook do Discord para notificações de pedidos'
      });

      // Save Telegram
      await api.post('/settings', {
        key: 'telegramToken',
        value: telegramToken.trim(),
        description: 'Token do Bot do Telegram para notificações'
      });
      await api.post('/settings', {
        key: 'telegramChatId',
        value: telegramChatId.trim(),
        description: 'Chat ID do Telegram para notificações'
      });

      // Save User Notice (push notification para os clientes)
      await api.post('/settings', {
        key: 'userNoticeEnabled',
        value: userNoticeEnabled ? 'true' : 'false',
        description: 'Se o aviso/push notification deve aparecer para os clientes'
      });
      await api.post('/settings', {
        key: 'userNoticeTitle',
        value: userNoticeTitle.trim(),
        description: 'Título do aviso exibido aos clientes'
      });
      await api.post('/settings', {
        key: 'userNoticeMessage',
        value: userNoticeMessage.trim(),
        description: 'Mensagem do aviso exibido aos clientes'
      });

      setSuccess('Configurações salvas com sucesso!');
      await fetchSettings();
    } catch (err) {
      console.error('Error saving settings:', err);
      setError(err.response?.data?.error || 'Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <>
        <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css" />
        <link href="https://fonts.googleapis.com/css2?family=Lexend:wght@100..900&display=swap" rel="stylesheet" />
        <style>{`
          .ast-loading { min-height: 100vh; background: #f5f5f5; display: flex; align-items: center; justify-content: center; font-family: 'Lexend', sans-serif; }
          .ast-loading-spinner { width: 50px; height: 50px; border: 4px solid rgba(255,153,0,0.2); border-top-color: #f90; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 16px; }
          @keyframes spin { to { transform: rotate(360deg); } }
          .ast-loading p { color: #666; font-weight: 500; }
        `}</style>
        <div className="ast-loading">
          <div style={{ textAlign: 'center' }}>
            <div className="ast-loading-spinner"></div>
            <p>Carregando configurações...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css" />
      <link href="https://fonts.googleapis.com/css2?family=Lexend:wght@100..900&display=swap" rel="stylesheet" />
      <style>{`
        .ast *, .ast *::before, .ast *::after { margin: 0; padding: 0; box-sizing: border-box; }
        .ast { font-family: 'Lexend', sans-serif; color: #555; background: #f5f5f5; min-height: 100vh; overflow-x: hidden; -webkit-font-smoothing: antialiased; }
        
        /* ===== HEADER ===== */
        .ast-header {
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
          padding: 18px 30px; display: flex; align-items: center; gap: 20px;
          box-shadow: 0 4px 25px rgba(0,0,0,0.15); position: sticky; top: 0; z-index: 1000;
        }
        .ast-header-logo { width: 40px; height: 40px; flex-shrink: 0; }
        .ast-header-logo img { width: 100%; height: 100%; object-fit: contain; }
        .ast-header-title { font-size: 22px; font-weight: 700; color: #fff; flex: 1; }
        .ast-header-back {
          display: flex; align-items: center; gap: 8px; color: #f90;
          cursor: pointer; transition: all 0.3s; font-weight: 500;
        }
        .ast-header-back:hover { color: #fff; transform: translateX(-3px); }

        /* ===== MAIN ===== */
        .ast-main { max-width: 700px; margin: 0 auto; padding: 35px 20px; }
        .ast-alert { padding: 14px 18px; border-radius: 12px; margin-bottom: 25px; }
        .ast-alert.success { background: rgba(56,161,105,0.1); border-left: 4px solid #38a169; color: #38a169; }
        .ast-alert.error { background: rgba(229,62,62,0.1); border-left: 4px solid #e53e3e; color: #e53e3e; }

        .ast-card {
          background: #fff; border-radius: 20px; padding: 40px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.06); border: 1px solid rgba(0,0,0,0.04);
        }
        .ast-card-header {
          display: flex; align-items: center; gap: 20px;
          margin-bottom: 32px; padding-bottom: 26px;
          border-bottom: 1px solid rgba(255,153,0,0.15);
          position: relative;
        }
        .ast-card-header::after {
          content: ''; position: absolute; bottom: -1px; left: 0; width: 70px; height: 3px;
          background: linear-gradient(90deg, #f90, #ff6600);
          border-radius: 3px;
        }
        .ast-card-icon {
          width: 70px; height: 70px;
          background: linear-gradient(135deg, #f90, #ff6600);
          border-radius: 18px;
          display: flex; align-items: center; justify-content: center;
          box-shadow: 0 8px 28px rgba(255,153,0,0.40),
                      inset 0 1px 0 rgba(255,255,255,0.25);
          position: relative;
          flex-shrink: 0;
          transition: transform 0.4s ease;
        }
        .ast-card-icon::before {
          content: ''; position: absolute; inset: -4px;
          border-radius: 22px;
          background: linear-gradient(135deg, rgba(255,153,0,0.28), rgba(255,102,0,0.15));
          z-index: -1; filter: blur(10px);
        }
        .ast-card-icon:hover { transform: scale(1.06) rotate(45deg); }
        .ast-card-icon i {
          font-size: 30px; color: #fff;
          filter: drop-shadow(0 2px 3px rgba(0,0,0,0.25));
          transition: transform 0.4s ease;
        }
        .ast-card-icon:hover i { transform: rotate(-45deg); }
        .ast-card-title h2 {
          font-size: 28px; font-weight: 800; color: #1a1a2e;
          letter-spacing: -0.3px; line-height: 1.2;
        }
        .ast-card-title p {
          color: #64748b; font-size: 15px; margin-top: 6px;
          font-weight: 500;
        }

        .ast-section { margin-bottom: 35px; padding-bottom: 35px; border-bottom: 2px solid #f0f0f0; }
        .ast-section:last-of-type { border-bottom: none; margin-bottom: 0; padding-bottom: 0; }
        .ast-section-title { font-size: 18px; font-weight: 700; color: #333; margin-bottom: 20px; display: flex; align-items: center; gap: 10px; }
        .ast-section-title i { color: #f90; }
        .ast-section-subtitle { font-size: 13px; color: #888; margin-bottom: 20px; }
        
        .ast-whatsapp-input { direction: ltr; text-align: left; font-family: monospace; }
        .ast-input-error { border-color: #e53e3e !important; }
        .ast-input-error:focus { box-shadow: 0 0 0 3px rgba(229,62,62,0.15) !important; }
        .ast-field-error { color: #e53e3e; font-size: 12px; margin-top: 6px; }

        .ast-form-group { margin-bottom: 25px; }
        .ast-form-group label { display: block; font-size: 14px; font-weight: 600; color: #666; margin-bottom: 10px; }
        .ast-form-input {
          width: 100%; padding: 14px 18px; border: 2px solid #e0e0e0; border-radius: 12px;
          font-size: 15px; outline: none; transition: all 0.3s; font-family: 'Lexend', sans-serif; resize: none;
        }
        .ast-form-input:focus { border-color: #f90; box-shadow: 0 0 0 3px rgba(255,153,0,0.15); }
        .ast-form-hint { font-size: 13px; color: #999; margin-top: 10px; }

        /* Payment mode toggle */
        .ast-mode-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        @media (max-width: 640px) { .ast-mode-grid { grid-template-columns: 1fr; } }
        .ast-mode-card {
          background: #fff; border: 2px solid #e0e0e0; border-radius: 14px;
          padding: 18px; cursor: pointer; transition: all 0.25s ease;
          display: flex; align-items: flex-start; gap: 14px; text-align: left;
          font-family: inherit;
        }
        .ast-mode-card:hover { border-color: #f90; background: #fff5e6; }
        .ast-mode-card.is-active {
          border-color: #f90;
          background: linear-gradient(135deg, #fff5e6, #ffedd5);
          box-shadow: 0 4px 15px rgba(255,153,0,0.15);
        }
        .ast-mode-icon {
          width: 44px; height: 44px; flex-shrink: 0;
          background: linear-gradient(135deg, #f90, #ff6600);
          color: #fff; border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          font-size: 20px;
          box-shadow: 0 3px 10px rgba(255,153,0,0.3);
        }
        .ast-mode-icon-manual {
          background: linear-gradient(135deg, #4a5568, #2d3748);
          box-shadow: 0 3px 10px rgba(74,85,104,0.3);
        }
        .ast-mode-content { flex: 1; }
        .ast-mode-title { font-size: 15px; font-weight: 700; color: #222; margin-bottom: 6px; display: flex; align-items: center; justify-content: space-between; gap: 8px; flex-wrap: wrap; }
        .ast-mode-active { font-size: 11px; font-weight: 600; color: #38a169; background: #f0fff4; padding: 3px 8px; border-radius: 999px; display: inline-flex; align-items: center; gap: 4px; }
        .ast-mode-active i { font-size: 10px; }
        .ast-mode-desc { font-size: 13px; color: #666; line-height: 1.5; }

        /* Toggle do aviso */
        .ast-toggle-row {
          display: flex; align-items: center; gap: 12px; cursor: pointer;
          padding: 12px 16px; background: #f8f9fa; border-radius: 12px; margin-bottom: 20px;
          font-size: 14px; font-weight: 600; color: #444;
        }
        .ast-toggle-row input { width: 18px; height: 18px; accent-color: #f90; cursor: pointer; }

        /* Preview do aviso */
        .ast-notice-preview-label { font-size: 12px; font-weight: 700; color: #999; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 10px; }
        .ast-notice-preview-card {
          display: flex; gap: 12px; background: #fff; border: 1px solid rgba(255,153,0,0.25);
          border-radius: 14px; padding: 14px 16px; box-shadow: 0 6px 20px rgba(0,0,0,0.08);
        }
        .ast-notice-preview-icon {
          width: 36px; height: 36px; border-radius: 10px; flex-shrink: 0;
          background: linear-gradient(135deg, #f90, #ff6600); color: #fff;
          display: flex; align-items: center; justify-content: center; font-size: 15px;
        }
        .ast-notice-preview-title { font-size: 14px; font-weight: 800; color: #1a1a2e; margin-bottom: 3px; }
        .ast-notice-preview-msg { font-size: 13px; color: #555; line-height: 1.5; white-space: pre-wrap; }

        .ast-submit-btn {
          width: 100%; background: linear-gradient(135deg, #f90, #ff6600); color: #fff;
          padding: 16px; border-radius: 12px; border: none; cursor: pointer; font-weight: 700;
          font-size: 17px; transition: all 0.3s; box-shadow: 0 4px 15px rgba(255,153,0,0.3);
          display: flex; align-items: center; justify-content: center; gap: 10px;
        }
        .ast-submit-btn:hover { transform: translateY(-2px); box-shadow: 0 6px 25px rgba(255,153,0,0.4); }
        .ast-submit-btn:disabled { background: #999; cursor: not-allowed; box-shadow: none; }

        @keyframes spin { to { transform: rotate(360deg); } }

        @media (max-width: 640px) {
          .ast-header { padding: 15px 20px; }
          .ast-header-title { font-size: 18px; }
          .ast-card { padding: 25px 20px; }
          .ast-card-header { flex-direction: column; align-items: flex-start; }
          .ast-card-title h2 { font-size: 22px; }
        }
      `}</style>

      <div className="ast">
        {/* Header */}
        <header className="ast-header">
          <div className="ast-header-logo">
            <img src="/images/comex.png" alt="COMEX" />
          </div>
          <h1 className="ast-header-title">Configurações do Sistema</h1>
          {/* Botão "Voltar" removido — navegação já fica na sidebar (AdminLayout) */}
        </header>

        {/* Main */}
        <main className="ast-main">
          {error && <div className="ast-alert error">{error}</div>}
          {success && <div className="ast-alert success">{success}</div>}

          <div className="ast-card">
            <div className="ast-card-header">
              <div className="ast-card-icon"><i className="fas fa-cog"></i></div>
              <div className="ast-card-title">
                <h2>Configurações do Sistema</h2>
                <p>Configure as opções do sistema</p>
              </div>
            </div>

            <form onSubmit={handleSubmit}>
              {/* WhatsApp Support Section */}
              <div className="ast-section">
                <div className="ast-section-title"><i className="fab fa-whatsapp"></i> Suporte WhatsApp</div>
                <p className="ast-section-subtitle">Número que aparecerá no ícone flutuante de suporte. Formato: 55 + DDD + Número (ex: 5511997547939)</p>
                
                <div className="ast-form-group">
                  <label>Número do WhatsApp</label>
                  <input
                    type="text"
                    value={whatsappNumber}
                    onChange={(e) => {
                      setWhatsappNumber(e.target.value);
                      setWhatsappError('');
                    }}
                    className={`ast-form-input ast-whatsapp-input ${whatsappError ? 'ast-input-error' : ''}`}
                    placeholder="5511999999999"
                    maxLength={14}
                  />
                  {whatsappError && <p className="ast-field-error"><i className="fas fa-exclamation-circle"></i> {whatsappError}</p>}
                  <p className="ast-form-hint">Apenas números. Exemplo: 5511997547939 (55 = Brasil, 11 = DDD, 997547939 = número)</p>
                </div>
              </div>

              {/* PIX Section */}
              <div className="ast-section">
                <div className="ast-section-title"><i className="fas fa-qrcode"></i> Configurações de Pagamento</div>
                <p className="ast-section-subtitle">Escolha como os pagamentos de créditos serão recebidos.</p>

                <div className="ast-form-group">
                  <label>Modo de Pagamento</label>
                  <div className="ast-mode-grid">
                    <button
                      type="button"
                      className={`ast-mode-card ${paymentMode === 'gateway' ? 'is-active' : ''}`}
                      onClick={() => setPaymentMode('gateway')}
                    >
                      <div className="ast-mode-icon"><i className="fas fa-bolt"></i></div>
                      <div className="ast-mode-content">
                        <div className="ast-mode-title">Gateway PIX (Automático)
                          {paymentMode === 'gateway' && <span className="ast-mode-active"><i className="fas fa-check-circle"></i> Ativo</span>}
                        </div>
                        <div className="ast-mode-desc">PIX gerado pela AtivoPay. Créditos liberados automaticamente ao detectar pagamento. Cliente não precisa enviar comprovante.</div>
                      </div>
                    </button>

                    <button
                      type="button"
                      className={`ast-mode-card ${paymentMode === 'pix_key' ? 'is-active' : ''}`}
                      onClick={() => setPaymentMode('pix_key')}
                    >
                      <div className="ast-mode-icon ast-mode-icon-manual"><i className="fas fa-key"></i></div>
                      <div className="ast-mode-content">
                        <div className="ast-mode-title">Chave PIX (Manual)
                          {paymentMode === 'pix_key' && <span className="ast-mode-active"><i className="fas fa-check-circle"></i> Ativo</span>}
                        </div>
                        <div className="ast-mode-desc">PIX gerado com sua chave fixa. Cliente envia comprovante via WhatsApp e você aprova manualmente em “Gerenciar Vendas”.</div>
                      </div>
                    </button>
                  </div>
                </div>

                {paymentMode === 'pix_key' && (
                  <div className="ast-form-group">
                    <label>Chave PIX</label>
                    <textarea
                      value={pixKey} onChange={(e) => setPixKey(e.target.value)} rows={4}
                      className="ast-form-input" placeholder="Cole sua chave PIX aqui (chave aleatória, email, telefone ou CPF/CNPJ)"
                    />
                    <p className="ast-form-hint">Esta chave será usada para gerar QR Codes PIX. Necessária apenas no modo manual.</p>
                  </div>
                )}
                {paymentMode === 'gateway' && (
                  <p className="ast-form-hint" style={{ background: '#fff5e6', padding: '12px 14px', borderRadius: '10px', borderLeft: '3px solid #f90' }}>
                    <i className="fas fa-info-circle" style={{ color: '#f90', marginRight: 6 }}></i>
                    No modo Gateway, as cobranças são criadas dinamicamente pela AtivoPay. Cada PIX é único por pedido. A chave PIX fixa abaixo não é utilizada.
                  </p>
                )}
              </div>

              {/* Discord Notifications Section */}
              <div className="ast-section">
                <div className="ast-section-title"><i className="fab fa-discord"></i> Notificações Discord</div>
                <p className="ast-section-subtitle">Receba uma mensagem no Discord toda vez que um cliente fizer um pedido de créditos.</p>
                <div className="ast-form-group">
                  <label>URL do Webhook Discord</label>
                  <input type="text" value={discordWebhook} onChange={(e) => setDiscordWebhook(e.target.value)}
                    className="ast-form-input" placeholder="https://discord.com/api/webhooks/..." />
                  <p className="ast-form-hint">
                    {discordWebhook ? <span style={{color:'#5865F2'}}><i className="fab fa-discord"></i> Webhook configurado — notificações ativas</span> : 'Deixe em branco para desativar'}
                  </p>
                </div>
              </div>

              {/* Telegram Notifications Section */}
              <div className="ast-section">
                <div className="ast-section-title"><i className="fab fa-telegram"></i> Notificações Telegram</div>
                <p className="ast-section-subtitle">Receba mensagens no Telegram quando um cliente fizer um pedido. Crie um bot pelo @BotFather e cole o token abaixo.</p>
                <div className="ast-form-group">
                  <label>Token do Bot</label>
                  <input type="text" value={telegramToken} onChange={(e) => setTelegramToken(e.target.value)}
                    className="ast-form-input" placeholder="123456789:AAHabc..." />
                </div>
                <div className="ast-form-group">
                  <label>Chat ID</label>
                  <input type="text" value={telegramChatId} onChange={(e) => setTelegramChatId(e.target.value)}
                    className="ast-form-input" placeholder="1234567890" />
                  <p className="ast-form-hint">
                    {telegramToken && telegramChatId ? <span style={{color:'#0088cc'}}><i className="fab fa-telegram"></i> Telegram configurado — notificações ativas</span> : 'Preencha os dois campos para ativar'}
                  </p>
                </div>
              </div>

              {/* Aviso para usuários (push notification) */}
              <div className="ast-section">
                <div className="ast-section-title"><i className="fas fa-bullhorn"></i> Aviso para usuários</div>
                <p className="ast-section-subtitle">
                  Mostra um aviso estilo notificação no canto da tela, só para os clientes (nunca aparece no painel admin).
                  Útil para avisos temporários, como "integração Shopify em fase de teste".
                </p>

                <label className="ast-toggle-row">
                  <input
                    type="checkbox"
                    checked={userNoticeEnabled}
                    onChange={(e) => setUserNoticeEnabled(e.target.checked)}
                  />
                  <span>{userNoticeEnabled ? 'Aviso ativo — aparecendo para os clientes' : 'Aviso desativado'}</span>
                </label>

                <div className="ast-form-group">
                  <label>Título (opcional)</label>
                  <input
                    type="text"
                    value={userNoticeTitle}
                    onChange={(e) => setUserNoticeTitle(e.target.value)}
                    className="ast-form-input"
                    placeholder="Ex: Aviso importante"
                    maxLength={80}
                  />
                </div>

                <div className="ast-form-group">
                  <label>Mensagem</label>
                  <textarea
                    value={userNoticeMessage}
                    onChange={(e) => setUserNoticeMessage(e.target.value)}
                    rows={4}
                    className="ast-form-input"
                    placeholder="Ex: A integração com a Shopify está em fase de testes. Qualquer instabilidade, entre em contato pelo suporte."
                    maxLength={400}
                  />
                  <p className="ast-form-hint">{userNoticeMessage.length}/400 caracteres</p>
                </div>

                {userNoticeEnabled && userNoticeMessage.trim() && (
                  <div className="ast-notice-preview">
                    <div className="ast-notice-preview-label"><i className="fas fa-eye"></i> Pré-visualização</div>
                    <div className="ast-notice-preview-card">
                      <div className="ast-notice-preview-icon"><i className="fas fa-bullhorn"></i></div>
                      <div>
                        {userNoticeTitle.trim() && <div className="ast-notice-preview-title">{userNoticeTitle}</div>}
                        <div className="ast-notice-preview-msg">{userNoticeMessage}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <button type="submit" disabled={saving} className="ast-submit-btn">
                {saving ? <><i className="fas fa-spinner fa-spin"></i> Salvando...</> : <><i className="fas fa-save"></i> Salvar Configurações</>}
              </button>
            </form>
          </div>
        </main>
      </div>
    </>
  );
};

export default AdminSettings;
